/**
 * The main toolbar (reference 02, SPEC §3): undo/redo, transform tools, the
 * centered Play button (Play in Editor, reference 05), profile links and
 * Resume.pdf, which is always visible.
 *
 * Buttons dispatch commands. Their state is read from stores.
 */
import { useTourTarget } from "../tour/TourTargets";
import { useEditor } from "../EditorContext";
import { useEditorSettings, useUndoState, useWorldSnapshot } from "../hooks/editorHooks";
import { useStore } from "../hooks/useStore";
import { formatPlayClock } from "../play/playFormat";
import type { PlayState } from "../play/PlaySession";
import type { SettingsValues } from "../settings/EditorSettings";
import {
  CodeIcon,
  DownloadIcon,
  LinkIcon,
  MoveIcon,
  PauseIcon,
  PlayIcon,
  RedoIcon,
  RotateIcon,
  ScaleIcon,
  SelectIcon,
  StopIcon,
  UndoIcon,
} from "../ui/icons";
import type { IconComponent } from "../ui/icons";
import "./toolbar.css";

type Tool = SettingsValues["tool"];

const TOOLS: readonly { tool: Tool; label: string; key: string; Icon: IconComponent }[] = [
  { tool: "select", label: "Select", key: "Q", Icon: SelectIcon },
  { tool: "translate", label: "Move", key: "W", Icon: MoveIcon },
  { tool: "rotate", label: "Rotate", key: "E", Icon: RotateIcon },
  { tool: "scale", label: "Scale", key: "R", Icon: ScaleIcon },
];

export function Toolbar() {
  return (
    <div className="kn-toolbar" role="toolbar" aria-label="Main toolbar">
      <HistoryButtons />
      <span className="kn-toolbar__divider" aria-hidden="true" />
      <ToolButtons />
      <span className="kn-toolbar__spacer" />
      <PlayButton />
      <span className="kn-toolbar__spacer" />
      <ProfileLinks />
    </div>
  );
}

export function HistoryButtons() {
  const { dispatch } = useEditor();
  const undo = useUndoState();

  return (
    <div className="kn-toolbar__group">
      <IconButton
        label={undo.undoLabel ? `Undo ${undo.undoLabel} (Ctrl+Z)` : "Undo (Ctrl+Z)"}
        Icon={UndoIcon}
        disabled={!undo.canUndo}
        onClick={() => void dispatch({ type: "history.undo", payload: {}, origin: "toolbar" })}
      />
      <IconButton
        label={undo.redoLabel ? `Redo ${undo.redoLabel} (Ctrl+Shift+Z)` : "Redo (Ctrl+Shift+Z)"}
        Icon={RedoIcon}
        disabled={!undo.canRedo}
        onClick={() => void dispatch({ type: "history.redo", payload: {}, origin: "toolbar" })}
      />
    </div>
  );
}

function ToolButtons() {
  const { dispatch } = useEditor();
  const { tool } = useEditorSettings();

  return (
    <div className="kn-toolbar__group" role="radiogroup" aria-label="Transform tool">
      {TOOLS.map(({ tool: value, label, key, Icon }) => (
        <IconButton
          key={value}
          label={`${label} (${key})`}
          Icon={Icon}
          role="radio"
          active={tool === value}
          onClick={() =>
            void dispatch({
              type: "editor.setSetting",
              payload: { key: "tool", value },
              origin: "toolbar",
            })
          }
        />
      ))}
    </div>
  );
}

/**
 * Play in Editor: Play runs the level's game mode; while it runs, Pause
 * (or Resume), Stop and the play clock take its place (reference 05). A level
 * with no game mode can't play, and says so. There's no options menu beside
 * it: Unreal's picks where to play, and there's only this viewport.
 */
export function PlayButton() {
  const { dispatch, play } = useEditor();
  const state = useStore(play);
  // The tour's last step spotlights Play.
  const tourTarget = useTourTarget("play");
  // Re-read on level changes: whether there's a mode to play depends on the level.
  useWorldSnapshot();
  if (state) return <PlayControls state={state} />;

  const mode = play.modeOfLevel();
  const hint = mode === null ? "This level has no game mode" : `Plays ${mode}`;
  return (
    <div className="kn-toolbar__play" title={hint} ref={tourTarget} data-testid="play">
      <button
        type="button"
        className="kn-toolbar__play-button"
        disabled={mode === null}
        aria-describedby="kn-play-hint"
        onClick={() => void dispatch({ type: "play.start", payload: {}, origin: "toolbar" })}
      >
        <PlayIcon size={14} />
        Play
      </button>
      <span id="kn-play-hint" className="kn-visually-hidden">
        {hint}
      </span>
    </div>
  );
}

function PlayControls({ state }: { state: PlayState }) {
  const { dispatch } = useEditor();
  const run = (type: string, payload: object) => {
    void dispatch({ type, payload, origin: "toolbar" });
  };

  return (
    <div className="kn-toolbar__play kn-toolbar__play--playing">
      <button
        type="button"
        className="kn-toolbar__play-control"
        aria-label={state.paused ? "Resume" : "Pause"}
        aria-pressed={state.paused}
        onClick={() => {
          run("play.pause", { paused: !state.paused });
        }}
      >
        {state.paused ? <PlayIcon size={14} /> : <PauseIcon size={14} />}
      </button>
      <button
        type="button"
        className="kn-toolbar__play-control kn-toolbar__play-control--stop"
        aria-label="Stop"
        onClick={() => {
          run("play.stop", {});
        }}
      >
        <StopIcon size={12} />
      </button>
      <span className="kn-toolbar__play-clock" role="timer" aria-label="Play time">
        PIE · {formatPlayClock(state.seconds)}
      </span>
    </div>
  );
}

function ProfileLinks() {
  const { links } = useEditor();

  return (
    <div className="kn-toolbar__group kn-toolbar__links">
      {links.github && (
        <a
          className="kn-toolbar__link"
          href={links.github}
          target="_blank"
          rel="noopener"
          title="GitHub"
        >
          <CodeIcon size={14} />
          <span className="kn-toolbar__link-label">GitHub</span>
        </a>
      )}
      {links.linkedin && (
        <a
          className="kn-toolbar__link"
          href={links.linkedin}
          target="_blank"
          rel="noopener"
          title="LinkedIn"
        >
          <LinkIcon size={14} />
          <span className="kn-toolbar__link-label">LinkedIn</span>
        </a>
      )}
      <a className="kn-toolbar__resume" href={links.resume} download>
        <DownloadIcon size={14} />
        Resume.pdf
      </a>
    </div>
  );
}

interface IconButtonProps {
  readonly label: string;
  readonly Icon: IconComponent;
  readonly onClick: () => void;
  readonly disabled?: boolean;
  readonly active?: boolean;
  readonly role?: "radio";
}

function IconButton({ label, Icon, onClick, disabled = false, active, role }: IconButtonProps) {
  return (
    <button
      type="button"
      className="kn-toolbar__icon"
      aria-label={label}
      title={label}
      disabled={disabled}
      data-active={active === true}
      {...(role && { role, "aria-checked": active === true })}
      onClick={onClick}
    >
      <Icon size={16} />
    </button>
  );
}
